import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthState, User } from '@/lib/types';
import { getCurrentUser, logout as authLogout } from '@/lib/auth';
import { trackAuthState } from '@/diagnostic';

interface AuthStore extends AuthState {
  login: (user: User) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
}

export const useAuth = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      login: (user) => {
        const newState = { user, isAuthenticated: true, isLoading: false };
        set(newState);
        
        // Track auth state for diagnostics
        trackAuthState(newState);
        
        console.log('[Auth] User logged in:', user.email);
      },
      logout: () => {
        authLogout();
        const newState = { user: null, isAuthenticated: false, isLoading: false };
        set(newState);
        
        // Track auth state for diagnostics
        trackAuthState(newState);
        
        console.log('[Auth] User logged out');
      },
      setLoading: (isLoading) => {
        const currentState = get();
        const newState = { ...currentState, isLoading };
        set(newState);
        
        // Track auth state for diagnostics
        trackAuthState(newState);
        
        console.log('[Auth] Loading state changed:', isLoading);
      },
    }),
    {
      name: 'auth-storage',
      onRehydrateStorage: () => (state) => {
        // Log rehydration
        console.log('[Auth] Rehydrating auth state from storage');
        
        if (state) {
          // Track initial state for diagnostics
          trackAuthState({
            user: state.user,
            isAuthenticated: state.isAuthenticated,
            isLoading: state.isLoading
          });
          
          // Verify auth state on rehydration
          getCurrentUser().then(user => {
            console.log('[Auth] Current user check result:', !!user);
            
            if (state) {
              state.setLoading(false);
              if (user) {
                state.login(user);
              } else {
                state.logout();
              }
            }
          }).catch(error => {
            console.error('[Auth] Error getting current user:', error);
            if (state) {
              state.setLoading(false);
              state.logout();
            }
          });
        }
      },
    }
  )
);